/* helper.ts */

/* Interfaces */
import {
  LinkProps,
  NodeProps,
  SankeyDiagramProps,
} from "src/shared/interfaces/charts/SankeyDiagram";

/**
 * Extracts a suffix in the format `TL<number>`
 * from a given string.
 *
 * Example: From "R-miniCHOP (TL1)", "TL1" will be
 * returned - otherwise "null", if no match is found.
 *
 * @param {string} name
 * @returns {string | null}
 */
export const getSuffix = (name: string): string | null => {
  const match = name.match(/\(TL\d+\)/);
  return match ? match[0] : null;
};

/**
 * Compute total amount of incoming (source) and outgoing (target)
 * links for each node. This helps to detect significant nodes and group
 * less relevant ones to "Other".
 *
 * Min and max values are used to set the range of the slider component.
 *
 * @param {LinkProps[]} links
 * @returns {Record<number, number>}
 */
export const computeNodeTotals = (
  links: LinkProps[]
): Record<number, number> => {
  const nodeTotals: Record<number, number> = {};

  links.forEach(({ source, target, value }) => {
    nodeTotals[source] = (nodeTotals[source] ?? 0) + value;
    nodeTotals[target] = (nodeTotals[target] ?? 0) + value;
  });

  return nodeTotals;
};

/**
 * Identify nodes that should be collapsed into an "Other (TLn)" bucket.
 *
 * Nodes are eligible for grouping when:
 *  - They carry a TL suffix.
 *  - Their total link weight is below `minAbsoluteWidth`.
 *  - They are not currently expanded via `expandedOthers`.
 *  - They are not listed in `protectedNodeNames` (e.g., members of an
 *    expanded treatment group that must stay visible).
 *  - They are not flagged with `isTreatmentGroup` (synthetic group nodes
 *    created by `applyTreatmentGroupMapping` must never fall into Other).
 *
 * @param {Record<number, number>} nodeTotals - Sum of incoming + outgoing link values per node index
 * @param {number} minAbsoluteWidth - Minimum total weight to remain visible as a standalone node
 * @param {NodeProps[]} nodes - Full node list
 * @param {string[]} expandedOthers - Names of "Other (TLn)" buckets currently expanded by the user
 * @param {Set<string>} protectedNodeNames - Node names that must not be grouped into Other
 * @returns {{ nodesToGroup: Set<number>, nodeTLMap: Map<number, string> }}
 */
export const markToGroupNodes = (
  nodeTotals: Record<number, number>,
  minAbsoluteWidth: number,
  nodes: NodeProps[],
  expandedOthers: string[] = [],
  protectedNodeNames: Set<string> = new Set()
) => {
  const nodesToGroup = new Set<number>();
  const nodeTLMap = new Map<number, string>();

  nodes.forEach((node, index) => {
    const tl = getSuffix(node.name);
    if (tl) nodeTLMap.set(index, tl);

    const otherName = `Other ${tl}`;
    const isExpanded = expandedOthers.includes(otherName);

    if (
      tl &&
      nodeTotals[index] < minAbsoluteWidth &&
      !isExpanded &&
      !protectedNodeNames.has(node.name) &&
      !node.isTreatmentGroup
    ) {
      nodesToGroup.add(index);
    }
  });

  return {
    nodesToGroup,
    nodeTLMap,
  };
};

/**
 * Replace all source/target links of small nodes.
 *
 * @param {LinkProps[]} links
 * @param {NodeProps[]} groupedNodes
 * @param {Set<number>} nodesToGroup
 * @param {Map<number, string>} nodeTLMap
 * @returns {LinkProps[]}
 */
export const createGroupedLinks = (
  links: LinkProps[],
  groupedNodes: NodeProps[],
  nodesToGroup: Set<number>,
  nodeTLMap: Map<number, string>
): LinkProps[] => {
  const otherNodeIndexMap = new Map<string, number>();

  return links.map((link) => {
    const replace = (index: number): number => {
      if (!nodesToGroup.has(index)) return index;

      const tl = nodeTLMap.get(index);
      if (!tl) return index;

      const otherName = `Other ${tl}`;

      if (!otherNodeIndexMap.has(otherName)) {
        const newIndex = groupedNodes.length;
        groupedNodes.push({ name: otherName });
        otherNodeIndexMap.set(otherName, newIndex);
      }

      return otherNodeIndexMap.get(otherName)!;
    };

    return {
      source: replace(link.source),
      target: replace(link.target),
      value: link.value,
    };
  });
};

/**
 * Remove duplicate links and aggregate their values.
 */
export const aggregateDuplicateLinks = (
  groupedLinks: LinkProps[]
) => {
  const linkMap = new Map<string, LinkProps>();
  const usedNodeIndices = new Set<number>();

  for (const link of groupedLinks) {
    const key = `${link.source}-${link.target}`;
    if (!linkMap.has(key)) {
      linkMap.set(key, { ...link });
    } else {
      linkMap.get(key)!.value += link.value;
    }
  }
  const finalLinks = Array.from(linkMap.values());

  finalLinks.forEach((l) => {
    usedNodeIndices.add(l.source);
    usedNodeIndices.add(l.target);
  });

  return {
    usedNodeIndices,
    finalLinks,
  };
};

/**
 * Returns the base name of a Sankey node by stripping the
 * treatment-line suffix `(TLn)`, if present.
 *
 * Examples:
 *   "FCR (TL1)"     → "FCR"
 *   "R-Benda (TL2)" → "R-Benda"
 *   "FCR"           → "FCR"
 *
 * @param {string} name
 * @returns {string}
 */
export const getBaseName = (name: string): string =>
  name.replace(/\s*\(TL\d+\)$/, "").trim();

/**
 * Priority order for resolving the group of a combined treatment node whose
 * base name contains " + " (e.g. "Dara-RVd + Mel + Autogenous transplantation").
 * Groups that appear earlier in this list take precedence when different
 * components of the combined name map to different groups.
 *
 * Clinical rationale: the most novel/targeted agent in the combination is the
 * therapeutically defining element (e.g. daratumumab in "Dara-RVd + Mel").
 */
export const TREATMENT_GROUP_PRIORITY: string[] = [
  
  // MM
  "Dara-based",
  "Isa-based",
  "Elo-based",
  "CAR-T",
  "Bispecific antibody",
  "ADC (BCMA)",
  "Carfilzomib-based",
  "Bortezomib-based",
  "Ixazomib-based",
  "Lenalidomide-based",
  "Pomalidomide-based",
  "CELMoD",
  "Other IMiD",
  "Selinexor-based",
  "Venetoclax-based",
  "Transplant conditioning",
  "Transplant",
  "Salvage chemotherapy",
  "Supportive care",
  "Radiation",
  "Rituximab",
];

/**
 * Resolve the group for a **combined** base name (one that contains " + ").
 *
 * Each component of the combined name is looked up individually in `groupMap`.
 * Among the groups found, the one with the highest priority (lowest index in
 * `TREATMENT_GROUP_PRIORITY`) wins. Returns `undefined` when no component has
 * a mapping.
 *
 * @param {string} combinedBaseName - e.g. "Dara-RVd + Mel + Autogenous transplantation"
 * @param {Record<string,string>} groupMap
 * @returns {string | undefined}
 */
export const resolveGroupForCombinedName = (
  combinedBaseName: string,
  groupMap: Record<string, string>
): string | undefined => {
  const components = combinedBaseName
    .split(" + ")
    .map((c) => c.trim())
    .filter(Boolean);

  const candidateGroups = components
    .map((component) => groupMap[component])
    .filter((group): group is string => Boolean(group));

  if (candidateGroups.length === 0) return undefined;

  return candidateGroups.reduce((best, current) => {
    const bestIdx = TREATMENT_GROUP_PRIORITY.indexOf(best);
    const currentIdx = TREATMENT_GROUP_PRIORITY.indexOf(current);
    // Lower index = higher priority; unknown groups stay at the end
    const effectiveBest =
      bestIdx === -1 ? Infinity : bestIdx;
    const effectiveCurrent =
      currentIdx === -1 ? Infinity : currentIdx;
    return effectiveCurrent < effectiveBest ? current : best;
  });
};

/**
 * Resolve the full group-node name for a Sankey node, or `null` when the
 * node is not mapped to any treatment group.
 *
 * Combines the three lookup steps used throughout the grouping feature:
 * exact base-name match, combined-name (" + ") priority resolution, and
 * re-attachment of the `(TLn)` suffix.
 *
 * Examples (with { FCR: "FCR-Benda" }):
 *   "FCR (TL1)" → "FCR-Benda (TL1)"
 *   "FCR"       → "FCR-Benda"
 *   "R-CHOP"    → null
 *
 * @param {string} nodeName
 * @param {Record<string,string>} groupMap - { memberBaseName → groupBaseName }
 * @returns {string | null}
 */
export const resolveGroupNodeName = (
  nodeName: string,
  groupMap: Record<string, string>
): string | null => {
  const suffix = getSuffix(nodeName);
  const baseName = getBaseName(nodeName);

  const groupBaseName: string | undefined =
    groupMap[baseName] ??
    (baseName.includes(" + ")
      ? resolveGroupForCombinedName(baseName, groupMap)
      : undefined);

  if (!groupBaseName) return null;

  return suffix ? `${groupBaseName} ${suffix}` : groupBaseName;
};

/**
 * Reorder Sankey nodes so that members of each **expanded** treatment group
 * are contiguous, anchored at the position of the group's first member.
 *
 * The Sankey layout is configured with `nodeSort(null)` / `iterations(0)`,
 * so the vertical order of nodes within a column is exactly their order in
 * the input array. Expanded members keep their original (backend) indices in
 * `applyTreatmentGroupMapping`, which scatters them between unrelated nodes.
 * This pass walks the node list once and, on encountering the first member
 * of an expanded group, emits all of that group's members back-to-back
 * (preserving their relative order). All other nodes keep their relative
 * order; link indices are remapped through the resulting permutation.
 *
 * @param {SankeyDiagramProps} data        - Sankey data (already group-mapped)
 * @param {Record<string,string>} groupMap - { memberBaseName → groupBaseName }
 * @param {string[]} expandedGroups        - Full names of currently-expanded groups
 * @returns {SankeyDiagramProps}
 */
export const orderExpandedGroupMembers = (
  data: SankeyDiagramProps,
  groupMap: Record<string, string>,
  expandedGroups: string[] = []
): SankeyDiagramProps => {
  if (expandedGroups.length === 0) return data;

  const { nodes, links } = data;

  /* Full group-node name per node index, only for expanded groups. */
  const expandedGroupKeyOf = (
    node: NodeProps
  ): string | null => {
    if (node.isTreatmentGroup) return null;
    const groupNodeName = resolveGroupNodeName(
      node.name,
      groupMap
    );
    return groupNodeName &&
      expandedGroups.includes(groupNodeName)
      ? groupNodeName
      : null;
  };

  const membersByGroup = new Map<string, number[]>();
  nodes.forEach((node, index) => {
    const key = expandedGroupKeyOf(node);
    if (!key) return;
    const members = membersByGroup.get(key) ?? [];
    members.push(index);
    membersByGroup.set(key, members);
  });

  const order: number[] = [];
  const emitted = new Set<number>();
  nodes.forEach((node, index) => {
    if (emitted.has(index)) return;
    const key = expandedGroupKeyOf(node);
    const block = key ? membersByGroup.get(key)! : [index];
    block.forEach((memberIndex) => {
      emitted.add(memberIndex);
      order.push(memberIndex);
    });
  });

  const oldToNewIndexMap = new Map<number, number>();
  order.forEach((oldIndex, newIndex) =>
    oldToNewIndexMap.set(oldIndex, newIndex)
  );

  return {
    nodes: order.map((oldIndex) => ({ ...nodes[oldIndex] })),
    links: links.map((link) => ({
      source: oldToNewIndexMap.get(link.source)!,
      target: oldToNewIndexMap.get(link.target)!,
      value: link.value,
    })),
  };
};

/**
 * Apply a treatment-group mapping to a Sankey diagram.
 *
 * Nodes whose base name (i.e. name without the `(TLn)` suffix) is a
 * **key** in `groupMap` are merged into a single group node whose name
 * is `<groupValue> <suffix>`.  Counts on all affected links are summed.
 *
 * **Combined names**: When the base name contains `" + "` (produced by the
 * backend when multiple episodes share the same treatment line), each component
 * is looked up individually in `groupMap` and the highest-priority group wins
 * (see `resolveGroupForCombinedName`).
 *
 * When the resulting group node's full name is listed in `expandedGroups`
 * the original member nodes are kept as individual nodes instead, so the
 * user can see each member separately.
 *
 * Non-mapped nodes pass through unchanged.
 *
 * @param {SankeyDiagramProps} data           - Original Sankey data
 * @param {Record<string,string>} groupMap    - { memberBaseName → groupBaseName }
 * @param {string[]} expandedGroups           - Full names of currently-expanded groups
 * @returns {SankeyDiagramProps}
 */
export const applyTreatmentGroupMapping = (
  data: SankeyDiagramProps,
  groupMap: Record<string, string>,
  expandedGroups: string[] = []
): SankeyDiagramProps => {
  const { nodes, links } = data;

  /* Start with a copy of all original node objects (preserves any extra props). */
  const groupedNodes: NodeProps[] = nodes.map((n) => ({
    ...n,
  }));
  /* Tracks indices of nodes that are synthetic group nodes (not from original data). */
  const syntheticGroupIndices = new Set<number>();
  /* Map from group-node full name → index in groupedNodes */
  const groupNodeIndexMap = new Map<string, number>();
  /* Map from original node index → remapped index */
  const nodeIndexRemap = new Map<number, number>();

  nodes.forEach((node, index) => {
    /* Resolve the group: exact match first, then combined-name fallback,
     * preserving the TL suffix (e.g. "FCR-Benda (TL1)"). */
    const groupNodeName = resolveGroupNodeName(
      node.name,
      groupMap
    );

    if (groupNodeName) {
      if (expandedGroups.includes(groupNodeName)) {
        /* Group is expanded → show original node unchanged. */
        nodeIndexRemap.set(index, index);
      } else {
        /* Group is collapsed → merge into the group node. */
        if (!groupNodeIndexMap.has(groupNodeName)) {
          /*
           * If the original data already contains a real node whose name
           * matches the synthetic group name, skip the mapping entirely for
           * all members of this group. Reusing that slot would silently
           * inflate link totals by merging distinct treatments; creating a
           * second node with the same name would break d3's data-join
           * keying. Leaving members as individual nodes is the safest option.
           */
          const collides = groupedNodes.some(
            (n) => n.name === groupNodeName
          );
          if (collides) {
            nodeIndexRemap.set(index, index);
            return; // skip this member — leave it as-is
          }

          /*
           * Place the group node at the current (first-member) index so that
           * its vertical position in the Sankey matches where the members
           * appear when the group is expanded. The original member at this
           * slot is no longer referenced by any link (links are remapped
           * below) so overwriting it is safe.
           */
          groupedNodes[index] = { name: groupNodeName };
          groupNodeIndexMap.set(groupNodeName, index);
          syntheticGroupIndices.add(index);
        }
        nodeIndexRemap.set(
          index,
          groupNodeIndexMap.get(groupNodeName)!
        );
      }
    } else {
      nodeIndexRemap.set(index, index);
    }
  });

  /* Stamp the isTreatmentGroup flag on synthetic group nodes. */
  syntheticGroupIndices.forEach((i) => {
    groupedNodes[i] = {
      ...groupedNodes[i],
      isTreatmentGroup: true,
    };
  });

  const newNodes: NodeProps[] = groupedNodes;

  const newLinks: LinkProps[] = links
    .map((link) => ({
      source:
        nodeIndexRemap.get(link.source) ?? link.source,
      target:
        nodeIndexRemap.get(link.target) ?? link.target,
      value: link.value,
    }))
    /* Drop self-links produced when both ends of a link collapse to the
     * same group node — d3-sankey cannot render them. */
    .filter((link) => link.source !== link.target);

  const { finalLinks } = aggregateDuplicateLinks(newLinks);
  const reindexed = reindexSankeyDiagram(
    newNodes,
    finalLinks
  );

  /* Pull members of expanded groups together — without this they keep
   * their scattered backend positions (see orderExpandedGroupMembers). */
  return orderExpandedGroupMembers(
    reindexed,
    groupMap,
    expandedGroups
  );
};

export const reindexSankeyDiagram = (
  nodes: NodeProps[],
  links: LinkProps[]
) => {
  const usedNodeIndices = new Set<number>();

  links.forEach((l) => {
    usedNodeIndices.add(l.source);
    usedNodeIndices.add(l.target);
  });

  const ensuredNodes: NodeProps[] = [];
  const oldToNewIndexMap = new Map<number, number>();

  [...usedNodeIndices].sort((a, b) => a - b).forEach((oldIndex) => {
    const newIndex = ensuredNodes.length;
    oldToNewIndexMap.set(oldIndex, newIndex);
    ensuredNodes.push({ ...nodes[oldIndex] });
  });

  const cleanedLinks = links
    .map((link) => {
      const source = oldToNewIndexMap.get(link.source);
      const target = oldToNewIndexMap.get(link.target);
      if (source === undefined || target === undefined)
        return null;
      return { source, target, value: link.value };
    })
    .filter((l): l is LinkProps => l !== null);

  return {
    nodes: ensuredNodes,
    links: cleanedLinks,
  };
};
